import { Injectable, inject } from '@angular/core';
import type { DeviceInfo } from '@core/remito.model';
import { LogService } from './log.service';

@Injectable({ providedIn: 'root' })
export class DeviceInfoService {
  private readonly logger = inject(LogService);

  getDeviceInfo(): Promise<DeviceInfo> {
    const userAgent = navigator.userAgent;
    const deviceInfo = {
      deviceType: this.getDeviceType(),
      os: this.getOS(),
      version: this.getOSVersion(),
      mobile: this.isMobile(),
      resolution: this.getScreenResolution(),
      networkType: this.getNetworkType(),
      timeHour: this.getHour(),
      timezone: this.getTimezone()
    };

    this.logger.debug('[DeviceInfoService] Información del dispositivo recolectada', {
      userAgent: userAgent.substring(0, 100) + '...',
      detectedDeviceInfo: deviceInfo,
      platform: navigator.platform,
      userAgentParsed: {
        isAndroid: /Android/i.test(userAgent),
        isIOS: /iPhone|iPad|iPod/i.test(userAgent),
        isWindows: /Windows/i.test(userAgent),
        isMac: /Mac/i.test(userAgent),
        isLinux: /Linux/i.test(userAgent)
      }
    });

    return Promise.resolve(deviceInfo);
  }

  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const userAgent = navigator.userAgent;

    this.logger.debug('[DeviceInfoService] Detectando tipo de dispositivo', {
      userAgent: userAgent.substring(0, 100)
    });

    // Detectar Android primero (es más común que iOS)
    if (/Android/i.test(userAgent)) {
      const isTablet = /Tablet|Pad/i.test(userAgent) ||
                       navigator.maxTouchPoints > 1 && window.screen.width > window.screen.height;

      const deviceType = isTablet ? 'tablet' : 'mobile';
      this.logger.debug('[DeviceInfoService] Android detectado', { deviceType });
      return deviceType;
    }

    // Detectar iOS (iPhone/iPad/iPod) - segundo lugar porque es más específico que desktop
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      const isTablet = /iPad/i.test(userAgent) ||
                       (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                       (navigator.platform === 'iPhone' && navigator.maxTouchPoints === 1);

      const deviceType = isTablet ? 'tablet' : 'mobile';
      this.logger.debug('[DeviceInfoService] iOS detectado', { deviceType });
      return deviceType;
    }

    // Detectar Windows (la mayoría de los desktops)
    if (/Windows/i.test(userAgent)) {
      this.logger.debug('[DeviceInfoService] Windows detectado', { deviceType: 'desktop' });
      return 'desktop';
    }

    // Detectar macOS
    if (/Mac/i.test(userAgent)) {
      this.logger.debug('[DeviceInfoService] macOS detectado', { deviceType: 'desktop' });
      return 'desktop';
    }

    // Detectar Linux
    if (/Linux/i.test(userAgent)) {
      this.logger.debug('[DeviceInfoService] Linux detectado', { deviceType: 'desktop' });
      return 'desktop';
    }

    // Fallback para otros casos
    this.logger.debug('[DeviceInfoService] Detectando como desktop por defecto');
    return 'desktop';
  }

  private getOS(): string {
    const ua = navigator.userAgent;

    this.logger.debug('[DeviceInfoService] Detectando sistema operativo', {
      userAgent: ua.substring(0, 100)
    });

    // Detectar en orden de mayor a menor especificidad
    if (/Android/i.test(ua)) {
      this.logger.debug('[DeviceInfoService] Android detectado');
      return 'Android';
    }

    if (/iPhone|iPad|iPod/i.test(ua)) {
      this.logger.debug('[DeviceInfoService] iOS detectado');
      return 'iOS';
    }

    if (/Windows/i.test(ua)) {
      this.logger.debug('[DeviceInfoService] Windows detectado');
      return 'Windows';
    }

    if (/Mac/i.test(ua)) {
      this.logger.debug('[DeviceInfoService] macOS detectado');
      return 'macOS';
    }

    if (/Linux/i.test(ua)) {
      this.logger.debug('[DeviceInfoService] Linux detectado');
      return 'Linux';
    }

    this.logger.debug('[DeviceInfoService] Sistema operativo desconocido');
    return 'Unknown';
  }

  private getOSVersion(): string {
    const ua = navigator.userAgent;

    if (/Android/i.test(ua)) {
      const match = ua.match(/Android (\d+[\d.]*)/);
      const version = match ? match[1] : 'unknown';
      this.logger.debug('[DeviceInfoService] Versión Android detectada', { version });
      return version;
    }

    if (/iPhone|iPad|iPod/i.test(ua)) {
      const match = ua.match(/OS (\d+)_(\d+)/);
      const version = match ? `${match[1]}.${match[2]}` : 'unknown';
      this.logger.debug('[DeviceInfoService] Versión iOS detectada', { version });
      return version;
    }

    if (/Windows/i.test(ua)) {
      const match = ua.match(/Windows NT (\d+\.\d+)/);
      const version = match ? match[1] : 'unknown';
      this.logger.debug('[DeviceInfoService] Versión Windows detectada', { version });
      return version;
    }

    if (/Mac/i.test(ua)) {
      const match = ua.match(/Mac OS X (\d+_\d+)/);
      const version = match ? match[1].replace('_', '.') : 'unknown';
      this.logger.debug('[DeviceInfoService] Versión macOS detectada', { version });
      return version;
    }

    this.logger.debug('[DeviceInfoService] Versión del sistema desconocida');
    return 'unknown';
  }

  private isMobile(): boolean {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

    this.logger.debug('[DeviceInfoService] Detectando como móvil', {
      isMobile,
      userAgent: userAgent.substring(0, 100)
    });

    return isMobile;
  }

  private getScreenResolution(): string {
    return `${window.screen.width}x${window.screen.height}`;
  }

  private getNetworkType(): string {
    if ('connection' in navigator) {
      const conn = navigator.connection as { type?: string; effectiveType?: string };

      // Prioridad 1: type (wifi, cellular, none)
      if (conn.type) {
        return conn.type;
      }

      // Prioridad 2: En desktop, si solo tenemos effectiveType, asumimos que hay conexión
      if (conn.effectiveType && !this.isMobile()) {
        return 'online';
      }

      // Prioridad 3: effectiveType solo si es móvil
      if (conn.effectiveType) {
        return conn.effectiveType;
      }
    }

    if (navigator.onLine) {
      return 'online';
    }

    return 'offline';
  }

  private getHour(): number {
    return new Date().getHours();
  }

  private getTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  }
}
